import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';

// Zentrale Formatierung für Währungen
const formatCurrency = (amount) => {
  return Number(amount).toFixed(2).replace('.', ',') + ' €';
};

export const generatePDFBlob = (gutschrift, settings) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const rightAlignX = pageWidth - margin;

  // 1. HEADER-LINIE (Designelement)
  doc.setDrawColor(180); // Sichtbares Grau
  doc.setLineWidth(0.2);
  doc.line(margin, 20, rightAlignX, 20);

  // 2. RECHTER BLOCK: Firmendaten
  doc.setFontSize(10);
  doc.setTextColor(0);
  let currentY = 30;

  const companyInfo = [
    settings.companyName,
    ...settings.address.split('\n'),
    `Tel: ${settings.phone}`,
    `E-Mail: ${settings.email}`,
    `USt-ID: ${settings.taxId}`,
    `GF: ${settings.ceo}`
  ];

  companyInfo.forEach(line => {
    doc.text(line, rightAlignX, currentY, { align: 'right' });
    currentY += 5;
  });

  currentY += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Gutschriftsnummer:', 140, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text(gutschrift.number, rightAlignX, currentY, { align: 'right' });

  currentY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('Datum:', 140, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text(format(new Date(gutschrift.date), 'dd.MM.yyyy'), rightAlignX, currentY, { align: 'right' });

  // 3. LINKER BLOCK: Empfänger & Titel
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('GUTSCHRIFT', margin, 50);

  doc.setFontSize(11);
  doc.text('Empfänger:', margin, 75);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(gutschrift.recipientName, margin, 82);
  
  const addrLines = gutschrift.recipientAddress.split('\n');
  addrLines.forEach((line, i) => {
    doc.text(line, margin, 87 + (i * 5));
  });

  // 4. Tabelle mit optimierten Spaltenbreiten
  const tableData = gutschrift.items.map((item, index) => [
    (index + 1).toString(),
    item.description,
    item.quantity.toString(),
    formatCurrency(item.price),
    formatCurrency(item.quantity * item.price)
  ]);

  doc.autoTable({
    startY: 110,
    // Zeilenumbrüche nur bei den Preis-Spalten
    head: [['Pos.', 'Beschreibung', 'Menge', 'Einzelpr.\n(Netto)', 'Gesamt\n(Netto)']],
    body: tableData,
    theme: 'grid',
    headStyles: { 
      fillColor: [30, 41, 59], 
      textColor: [255, 255, 255], 
      fontStyle: 'bold',
      halign: 'left',
      valign: 'middle'
    },
    columnStyles: {
      // Breiten angepasst, damit Header "Pos." und "Menge" nicht umbrechen
      // bei Standard-Padding (3) benötigt der Text etwas mehr Platz
      0: { cellWidth: 14, halign: 'center', textColor: [100, 100, 100] }, // War 10 -> Jetzt 14
      1: { cellWidth: 'auto' }, // Nimmt den Rest
      2: { cellWidth: 20, halign: 'center' }, // War 14 -> Jetzt 20
      3: { cellWidth: 22, halign: 'right' }, // Bleibt kompakt
      4: { cellWidth: 22, halign: 'right' }  // Bleibt kompakt
    },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      valign: 'middle'
    }
  });

  // 5. Summenblock
  const finalY = doc.lastAutoTable.finalY + 10;
  const netto = gutschrift.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const mwst = netto * 0.19;
  const brutto = netto + mwst;

  const labelX = 110;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.text('Zwischensumme (Netto):', labelX, finalY);
  doc.text(formatCurrency(netto), rightAlignX, finalY, { align: 'right' });

  doc.text('zzgl. 19% MwSt:', labelX, finalY + 6);
  doc.text(formatCurrency(mwst), rightAlignX, finalY + 6, { align: 'right' });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Gesamtbetrag (Brutto):', labelX, finalY + 14);
  doc.text(formatCurrency(brutto), rightAlignX, finalY + 14, { align: 'right' });

  // 6. FOOTER & Zusatztexte
  const footerY = 285;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60);
  
  // Leistungsdatum Hinweis
  doc.text('Rechnungsdatum entspricht dem Leistungsdatum, wenn nicht anders angegeben.', pageWidth / 2, footerY - 12, { align: 'center' });

  // FOOTER-LINIE
  doc.setDrawColor(180);
  doc.setLineWidth(0.2);
  doc.line(margin, footerY - 5, rightAlignX, footerY - 5);

  doc.setFontSize(7);
  doc.setTextColor(100);
  const legalText = `${settings.companyName} | ${settings.address.replace(/\n/g, ' | ')} | Geschäftsführer: ${settings.ceo} | ${settings.register} ${settings.hrb} | USt-ID: ${settings.taxId}`;
  
  doc.text(legalText, pageWidth / 2, footerY, { align: 'center' });

  return doc.output('blob');
};

export const generatePDF = async (gutschrift, settings) => {
  const blob = generatePDFBlob(gutschrift, settings);
  const fileName = `Gutschrift_${gutschrift.number}.pdf`;

  if ('showSaveFilePicker' in window) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [{
          description: 'PDF Document',
          accept: { 'application/pdf': ['.pdf'] },
        }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('File Picker failed, using fallback', err);
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};